/**
 * Author registry + parsing helpers for blog author avatars.
 *
 * Blog frontmatter stores `author` as a single free-text string. In practice
 * that string can hold MULTIPLE authors joined by "&", ",", or "and", and the
 * same person shows up under several spellings (nicknames, typos, trailing
 * spaces, accents). This module is the single source of truth that:
 *
 *   1. Splits a frontmatter author string into individual authors.
 *   2. Resolves each one to a canonical name + slug via the registry below,
 *      collapsing aliases/typos so one person maps to one avatar file.
 *   3. Produces an initials fallback + a stable color for authors with no photo.
 *
 * Avatar images live in `src/assets/authors/<slug>.{png,jpg,jpeg,webp}`.
 * The <AuthorAvatars> component queries that folder; any author whose slug has
 * no matching file renders the initials placeholder instead.
 *
 * To add/correct an author: edit the AUTHORS array. `aliases` should list every
 * raw spelling that appears in frontmatter so it folds into the canonical entry.
 */

/**
 * Canonical authors. `name` is the display name, `slug` is the avatar filename
 * base, `aliases` are alternate raw spellings found in blog frontmatter.
 */
export const AUTHORS = [
  { name: "Evan Rusackas", slug: "evan-rusackas" },
  { name: "Srini Kadamati", slug: "srini-kadamati" },
  {
    name: "Maxime Beauchemin",
    slug: "maxime-beauchemin",
    aliases: ["Max Beauchemin"],
  },
  { name: "Satoko Nakayama", slug: "satoko-nakayama" },
  { name: "Robert Stolz", slug: "robert-stolz" },
  { name: "Shreesham Mukherjee", slug: "shreesham-mukherjee" },
  {
    name: "Katia Zhivaikina",
    slug: "katia-zhivaikina",
    // "Ekaterina" is Katia's full first name; "Zhiavikina" is a transposition typo.
    aliases: ["Ekaterina Zhivaikina", "Katia Zhiavikina"],
  },
  { name: "Alan Hsia", slug: "alan-hsia" },
  { name: "Beto Dealmeida", slug: "beto-dealmeida" },
  { name: "Zane Aitken", slug: "zane-aitken" },
  { name: "Sophie You", slug: "sophie-you" },
  { name: "Eugenia Moreno", slug: "eugenia-moreno" },
  { name: "Elizabeth Thompson", slug: "elizabeth-thompson" },
  { name: "Rob DiCiuccio", slug: "rob-diciuccio" },
  { name: "Diego Pucci", slug: "diego-pucci" },
  { name: "Craig Rueda", slug: "craig-rueda" },
  { name: "Amin Ghadersohi", slug: "amin-ghadersohi" },
  { name: "Sida Shen", slug: "sida-shen" },
  { name: "Pierre Vanacker", slug: "pierre-vanacker" },
  {
    name: "Michael S. Molina",
    slug: "michael-s-molina",
    aliases: ["Michael Molina"],
  },
  { name: "Igor Lukanin", slug: "igor-lukanin" },
  { name: "Yevgeny Pats", slug: "yevgeny-pats" },
  { name: "Will Barrett", slug: "will-barrett" },
  { name: "Vitor Ávila", slug: "vitor-avila", aliases: ["Vitor Avila"] },
  { name: "Ville Brofeldt", slug: "ville-brofeldt" },
  { name: "Tai Dupree", slug: "tai-dupree" },
  { name: "Stephan Claus", slug: "stephan-claus" },
  { name: "Marija Selakovic", slug: "marija-selakovic" },
  { name: "Robert Hodges", slug: "robert-hodges" },
  { name: "Lior Gavish", slug: "lior-gavish" },
  { name: "Maria King-Smart", slug: "maria-king-smart" },
  { name: "Kasia Zając", slug: "kasia-zajac" },
  { name: "Juliann Forbes", slug: "juliann-forbes" },
  { name: "Joe Li", slug: "joe-li" },
  { name: "Anupreet Walia", slug: "anupreet-walia" },
  {
    name: "Jason Araos",
    slug: "jason-araos",
    aliases: ["Jason Araos Fuentes"],
  },
  { name: "Brian Sheehan", slug: "brian-sheehan" },
  { name: "Brian Olsen", slug: "brian-olsen" },
  { name: "Daniel Wegmann", slug: "daniel-wegmann" },
  { name: "Daniel Gaspar", slug: "daniel-gaspar" },
  { name: "Denis Polyakov", slug: "denis-polyakov" },
  { name: "Mikhail Stolpner", slug: "mikhail-stolpner" },
  { name: "Akshay Atrey", slug: "akshay-atrey" },
  { name: "Balachandar Seetharaman", slug: "balachandar-seetharaman" },
  { name: "Anna Lee", slug: "anna-lee" },
  { name: "Richard Fogaça Nienkotter", slug: "richard-fogaca-nienkotter" },
  {
    // Currently appears only in open blog PRs (e.g. #798); avatar is staged so
    // it resolves automatically once those merge.
    name: "Sebastien LeMenager",
    slug: "sebastien-lemenager",
    aliases: ["Seb LeMenager", "Sebastien Le Menager", "Sébastien Le Menager"],
  },
  // Teams / orgs — avatars optional; these fall back to initials or a logo.
  {
    name: "Preset Team",
    slug: "preset-team",
    aliases: ["Preset Community Team"],
  },
  { name: "Superset Community", slug: "superset-community" },
  { name: "Data Culture", slug: "data-culture" },
  {
    name: "The Data Renegades Podcast by Heavybit",
    slug: "data-renegades-podcast",
  },
  { name: "DatAgor 📊", slug: "datagor" },
]

/** Strip accents, lowercase, collapse whitespace — for alias matching. */
function normalize(str) {
  return str
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
}

/** kebab-case slug for authors not found in the registry. */
export function slugify(str) {
  return str
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// normalized spelling -> canonical author, built once from the registry.
const ALIAS_MAP = new Map()
for (const author of AUTHORS) {
  ALIAS_MAP.set(normalize(author.name), author)
  for (const alias of author.aliases || []) {
    ALIAS_MAP.set(normalize(alias), author)
  }
}

/**
 * Initials from a display name: first letter of the first and last "word",
 * ignoring punctuation/emoji. Single-word names yield one letter.
 */
export function getInitials(name) {
  const words = name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
    .filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0][0].toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/**
 * Deterministic, pleasant background color for an initials placeholder.
 * Same slug always yields the same hue so an author looks consistent.
 */
export function avatarColor(slug) {
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) % 360
  }
  return `hsl(${hash}, 45%, 52%)`
}

/**
 * Parse a frontmatter author string into an ordered list of distinct authors.
 *
 * @param {string} authorString e.g. "Maxime Beauchemin & Evan Rusackas"
 * @returns {Array<{name: string, slug: string, initials: string, color: string}>}
 */
export function parseAuthors(authorString) {
  if (!authorString || typeof authorString !== "string") return []

  const seen = new Set()
  const result = []

  for (const raw of authorString.split(/\s*&\s*|\s*,\s*|\s+and\s+/i)) {
    const token = raw.trim()
    if (!token) continue

    const canonical = ALIAS_MAP.get(normalize(token))
    const name = canonical ? canonical.name : token
    const slug = canonical ? canonical.slug : slugify(token)
    if (!slug || seen.has(slug)) continue
    seen.add(slug)

    result.push({
      name,
      slug,
      initials: getInitials(name),
      color: avatarColor(slug),
    })
  }

  return result
}
